import React, { useMemo, useRef } from 'react';

import {
	type LoomPluginOptionsWithoutProvider,
	type ButtonComponent as LoomPluginButtonComponent,
} from '@atlaskit/editor-plugin-loom';

import { expVal } from '@confluence/feature-experiments';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant.ts';
import type { LoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariantState.ts';

import type { UseLoomPluginOptionsProps } from '../loomEditorUtils-types.ts';

type UseLoomPlugin = {
	isPluginEnabled: boolean;
	pluginOptions: LoomPluginOptionsWithoutProvider;
};

const shouldRenderButton = (
	entryPointVariant: LoomEntryPointVariant['entryPointVariant'],
	appearance?: string,
): boolean => {
	if (appearance === 'comment') {
		return entryPointVariant !== 'NONE' && entryPointVariant !== 'CROSS_FLOW';
	}

	return entryPointVariant !== 'NONE';
};

export const useLoomPluginOptions = ({
	EditorButtonComponent,
	editorAPIRef,
	editorShouldShowToolbarLoomButton,
	appearance,
}: UseLoomPluginOptionsProps): UseLoomPlugin => {
	const { entryPointVariant } = useLoomEntryPointVariant();
	const entryPointVariantRef = useRef(entryPointVariant);
	/**
	 * The Loom plugin should be enabled for all non-disabled Loom entrypoints.
	 *
	 * This has to be determined on the initial call of the hook (due to the way plugin initialization works).
	 * It should also still be OK if the plugin is enabled even for edge cases where the entrypoint is
	 * asynchronously determined to be disabled (e.g., when a site has opted out of cross-flow ads) because:
	 *
	 * `useInitializeLoomPlugin` will handle the conditions for initializing the plugin (to enable recording) and
	 * `EditorButtonComponent` will handle conditional rendering (in some cases, even returning `null` to dynamically
	 * hide the button)
	 */
	const isPluginEnabled = entryPointVariant !== 'DISABLED';

	// [ED-27497] IMPORTANT: If you change the code below, please also update the
	// platform/packages/editor/editor-presets-confluence/src/presets/full-page/loomPluginOptions.ts
	const pluginOptions: LoomPluginOptionsWithoutProvider = useMemo(() => {
		return {
			renderButton: (ButtonComponent: LoomPluginButtonComponent) => {
				return (
					<EditorButtonComponent buttonComponent={ButtonComponent} editorAPIRef={editorAPIRef} />
				);
			},
			shouldRenderButton: expVal('platform_editor_toolbar_hide_overflow_menu', 'isEnabled', false)
				? () => shouldRenderButton(entryPointVariantRef.current, appearance)
				: undefined,
			...(editorShouldShowToolbarLoomButton !== undefined
				? { shouldShowToolbarButton: editorShouldShowToolbarLoomButton }
				: {}),
		};
		// Intentionally not recreating/updating the plugin options in order to remain consistent with
		// the behavior of the editor preset that uses this hook to generate the loom plugin options
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { isPluginEnabled, pluginOptions };
};
